import { useTranslation } from 'react-i18next';
import {
  PieceMetadataModel,
  PieceMetadataModelSummary,
  PropertyType,
} from '@activepieces/pieces-framework';
import { AVAILABLE_PIECES_TRANSLATE } from '../../../available-pieces-translate';

const getPieceNameSpace = (pieceName: string) => ({
  namespace: pieceName.replace('@activepieces/', ''),
});

const mapObjectValues = <V, R>(
  obj: Record<string, V>,
  fn: (val: V) => R,
): Record<string, R> => {
  return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, fn(v)]));
};

const isFullModel = (piece: AnyPieceModel): piece is PieceMetadataModel =>
  typeof piece.actions !== 'number' && typeof piece.triggers !== 'number';

type AnyPieceModel = PieceMetadataModel | PieceMetadataModelSummary;

export const usePieceTranslation = () => {
  const { t, i18n } = useTranslation();

  const loadNamespace = async (pieceName: string) => {
    const { namespace } = getPieceNameSpace(pieceName);
    const success = AVAILABLE_PIECES_TRANSLATE.includes(namespace)
      ? await i18n
          .loadNamespaces(namespace)
          .then(() => true)
          .catch(() => false)
      : false;
    return { success, namespace };
  };

  const translateCommon = (item: any, namespace: string) => ({
    ...item,
    displayName: t(item.displayName, { ns: namespace }),
    ...(item.description && {
      description: t(item.description, { ns: namespace }),
    }),
  });

  const translateProps = (
    props: Record<string, any>,
    namespace: string,
  ): Record<string, any> => {
    return mapObjectValues(props, (prop) => {
      const translated = translateCommon(prop, namespace);

      if (prop.type === PropertyType.ARRAY && prop.properties) {
        translated.properties = translateProps(prop.properties, namespace);
      }

      if (
        (prop.type === PropertyType.STATIC_DROPDOWN ||
          prop.type === PropertyType.STATIC_MULTI_SELECT_DROPDOWN) &&
        prop.options?.options
      ) {
        translated.options = {
          ...prop.options,
          options: prop.options.options.map((opt: Record<string, any>) => ({
            ...opt,
            ...(opt.label && { label: t(opt.label, { ns: namespace }) }),
          })),
        };
      }
      return translated;
    });
  };

  const translate = async <T extends AnyPieceModel>(piece: T): Promise<T> => {
    const { success, namespace } = await loadNamespace(piece.name);
    if (!success) return piece;

    const baseTranslation = {
      ...piece,
      displayName: t(piece.displayName, { ns: namespace }),
      description: t(piece.description, { ns: namespace }),
      ...(piece.auth && {
        auth: {
          ...translateCommon(piece.auth, namespace),
          ...('props' in piece.auth &&
            piece.auth.props && {
              props: translateProps(piece.auth.props, namespace),
            }),
        },
      }),
    };

    if (isFullModel(piece)) {
      return {
        ...baseTranslation,
        actions: mapObjectValues(piece.actions, (a) => ({
          ...translateCommon(a, namespace),
          props: translateProps(a.props, namespace),
        })),
        triggers: mapObjectValues(piece.triggers, (tr) => ({
          ...translateCommon(tr, namespace),
          props: translateProps(tr.props, namespace),
        })),
      } as T;
    } else {
      return {
        ...baseTranslation,
        suggestedActions: piece.suggestedActions?.map((a) =>
          translateCommon(a, namespace),
        ),
        suggestedTriggers: piece.suggestedTriggers?.map((t) =>
          translateCommon(t, namespace),
        ),
      } as T;
    }
  };

  const filterTranslatedPieces = (
    pieces: PieceMetadataModelSummary[],
    query: string,
  ): PieceMetadataModelSummary[] => {
    if (!query) return pieces;

    const lowerQuery = query.toLowerCase();
    const matches = (text?: string) =>
      text?.toLowerCase().includes(lowerQuery) ?? false;

    const PRIORITY = { NAME_START: 0, NAME: 1, ACTION: 2, DESC: 3 };

    const startsWith = (text?: string) =>
      text?.toLowerCase().startsWith(lowerQuery) ?? false;

    return pieces
      .flatMap((piece) => {
        if (startsWith(piece.displayName)) {
          return [{ piece, priority: PRIORITY.NAME_START }];
        }

        if (matches(piece.displayName)) {
          return [{ piece, priority: PRIORITY.NAME }];
        }

        const filteredActions = piece.suggestedActions?.filter(
          (a) => matches(a.displayName) || matches(a.description),
        ) ?? [];
        const filteredTriggers = piece.suggestedTriggers?.filter(
          (t) => matches(t.displayName) || matches(t.description),
        ) ?? [];

        const hasNameMatch =
          filteredActions.some((a) => matches(a.displayName)) ||
          filteredTriggers.some((t) => matches(t.displayName));

        if (hasNameMatch) {
          return [{
            piece: { ...piece, suggestedActions: filteredActions, suggestedTriggers: filteredTriggers },
            priority: PRIORITY.ACTION,
          }];
        }

        if (matches(piece.description)) {
          return [{ piece, priority: PRIORITY.DESC }];
        }

        if (filteredActions.length > 0 || filteredTriggers.length > 0) {
          return [{
            piece: { ...piece, suggestedActions: filteredActions, suggestedTriggers: filteredTriggers },
            priority: PRIORITY.DESC,
          }];
        }

        return [];
      })
      .sort((a, b) => a.priority - b.priority)
      .map((r) => r.piece);
  };

  return {
    translate,
    filterTranslatedPieces,
  };
};
